import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import i18n from '@/config/i18n.config'

import DentalHiringAnswers from '@/modules/dental/hiring/models/APP/DentalHiringAnswers'
import DentalHiringInsured from '@/modules/dental/hiring/models/APP/DentalHiringInsured'
import ENDPOINTS from '@/modules/core/constants/apiEndpoints'
import DentalConfigurations from '@/modules/dental/common/models/APP/DentalConfigurations'
import DentalInsuranceProcessStepEnum from '@/modules/dental/hiring/models/APP/DentalInsuranceProcessStepEnum'
import {
  ErrorDisplayTypeEnum,
  InsuranceProcessStatusEnum,
  UserRoleEnum
} from '@/modules/core/models/enums'
import MiddlewareService from '@/modules/core/services/MiddlewareService'
import ServiceError from '@/modules/core/models/errors/ServiceError'
import ModalButton from '@/modules/core/models/modalMessage/ModalButton'
import { useErrorManagerComposable } from '@/modules/core/composables/errorManagerComposable'
import { useToastStore } from '@/modules/core/stores/toastStore'
import { useLoadingStore } from '@/modules/core/stores/loadingStore'
import { useCommonStore } from '@/modules/core/stores/commonStore'
import DXPService from '@/modules/core/services/DXPService'
import DentalCommonMapper from '@/modules/dental/common/mappers'
import DentalMapper from '@/modules/dental/hiring/mappers'
import ProductId from '@/modules/common/common/models/DXP/ProductId'
import type DXPProduct from '@/modules/common/common/models/DXP/Product'
import CustomError from '@/modules/core/models/errors/CustomError'
import type DentalInsurance from '../../common/models/APP/DentalInsurance'
import type RaterPricing from '@/modules/common/common/models/MID/RaterPricing'
import { checkRecoveredQuotationBranch } from '@/modules/core/utils/BranchUtils'
import GetProductResponse from '@/modules/common/common/models/DXP/responses/GetProductResponse'

//TODO:we are dividing the store in two parts, one for rater and one for hiring
export const useDentalStore = defineStore('dental', () => {
  const errorManager = useErrorManagerComposable()
  const toastStore = useToastStore()
  const loadingStore = useLoadingStore()
  const commonStore = useCommonStore()

  const { t } = i18n.global

  const globalConfig = ref(new DentalConfigurations())
  const hiringAnswers = ref(new DentalHiringAnswers())

  const insuredsState = computed(() => {
    const insureds = hiringAnswers.value.insureds
    const insuredsComplete = insureds.map(
      (i: any) => !!i.documentDetails.documentNumber || i.documentDetails.documentType === 'MENOR'
    )
    const holder = hiringAnswers.value.holder.role
    const index = holder === UserRoleEnum.HOLDER_NOT_INSURED ? 0 : 1

    return insuredsComplete.slice(index, insureds.length)
  })

  const stepsBeforeSaveAndExit = computed(() => {
    const stepCode = hiringAnswers.value.processStatus?.stepCode ?? 0
    const role = hiringAnswers.value.holder.role

    const insuranceData = {
      label: t('copy_save-and-exit-modal_step-insurance'),
      check: true
    }

    const documentStep = {
      label: t('copy_save-and-exit-modal_step-document'),
      check: stepCode > DentalInsuranceProcessStepEnum.DOCUMENTS
    }

    const holderInsuredIndex = !insuredsState.value.length ? null : 1
    const holderStep = {
      label: t(`copy_save-and-exit-modal_step-holder-${role}`, { n: holderInsuredIndex }),
      check: stepCode > DentalInsuranceProcessStepEnum.HOLDER
    }

    const insuredsSteps: any = []
    hiringAnswers.value.insureds.map((insured, i) => {
      if (insured.role === UserRoleEnum.INSURED) {
        insuredsSteps.push({
          label: t('copy_save-and-exit-modal_step-insured', { n: i + 1 }),
          check: !!insured.userDetails.email
        })
      }
    })

    return [insuranceData, documentStep, holderStep, ...insuredsSteps]
  })

  function getBranchConfiguration() {
    return globalConfig.value
  }

  function setCurrentInsuredAnswers(answers: Partial<DentalHiringInsured>, insuredIndex: number) {
    if (insuredIndex === 0) {
      hiringAnswers.value.updateHolderAttributes(answers)
    } else {
      hiringAnswers.value.updateInsuredAttributes(insuredIndex, answers)
    }
  }

  function setGloballyAnswers(answers: Partial<DentalHiringAnswers>) {
    hiringAnswers.value.updateAttributes(answers)
  }

  function _getCurrentInsured(insuredIndex?: number | string) {
    if (insuredIndex === undefined) return null

    if (insuredIndex === 0 || insuredIndex === 'holder') return hiringAnswers.value.holder

    if (hiringAnswers.value.holder.role === UserRoleEnum.HOLDER_NOT_INSURED) {
      return hiringAnswers.value.insureds[(insuredIndex as number) - 1]
    }

    return hiringAnswers.value.insureds[insuredIndex as number]
  }

  function getHiringAnswers(insuredIndex?: number | string): DentalHiringAnswers {
    const currentInsured = _getCurrentInsured(insuredIndex)
    const answers = hiringAnswers.value as DentalHiringAnswers
    const insured = currentInsured as DentalHiringInsured
    answers.currentInsured = insured ? new DentalHiringInsured(insured) : null

    return answers
  }

  //** PRIVATE SUPPORT METHODS */
  async function _fetchDXPInsurances(products: ProductId[]): Promise<DXPProduct[]> {
    const endpoint = ENDPOINTS.DXP_GET_PRODUCT
    const request = DentalCommonMapper.GetProductsMapper.toRequest(products)
    const { error, data } = await DXPService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    return data.map((d: any) => new GetProductResponse(d).product)
  }

  function _loadCompleteSheetProducts(
    DXPProducts: DXPProduct[],
    MIDProducts: RaterPricing[]
  ): DentalInsurance[] {
    const products: DentalInsurance[] = []
    DXPProducts.forEach((DXPProduct) => {
      const MIDProduct = MIDProducts.find((p) => {
        return String(DXPProduct.productId) === String(p.productCode)
      })

      if (MIDProduct) {
        const insurance = DentalCommonMapper.GetProductsMapper.fromResponse(DXPProduct, MIDProduct)
        products.push(insurance)
      }
    })

    return products
  }

  async function loadConfiguration(reset = false) {
    try {
      if (globalConfig.value?.businessLineId && !reset) {
        hiringAnswers.value.businessLine = globalConfig.value.businessLineId
        return
      }
      const endpoint = ENDPOINTS.DXP_GET_BRANCH_DENTAL
      const { error, data } = await DXPService.get(endpoint)
      if (error) {
        throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.MODAL)
      }

      globalConfig.value = new DentalConfigurations(data)
      hiringAnswers.value.businessLine = globalConfig.value.businessLineId
    } catch (error) {
      loadingStore.stopLoading()
      errorManager.handleError(error)
    }
  }

  async function getNewRaterPrice(
    answers: DentalHiringAnswers,
    errorDisplayType: ErrorDisplayTypeEnum = ErrorDisplayTypeEnum.TOAST,
    isQuote: boolean = true,
    productCode: string | null | undefined = undefined
  ): Promise<DentalInsurance | null> {
    try {
      loadingStore.startLoading('common.loading.processing')

      const endpoint = ENDPOINTS.RATER_CALCULATE_PRICE_DENTAL
      const request = DentalMapper.GetPricingMapper.toRequest(
        answers,
        globalConfig.value.relationships,
        false,
        isQuote,
        commonStore.infoMediator?.id
      )
      const { error, data } = await MiddlewareService.post(endpoint, request)
      if (error) throw new ServiceError(data)

      const products = data.listPricing.map((l: RaterPricing) => {
        return new ProductId({
          productCode: l.productCode,
          clientCode: l.clientCode
        })
      })
      const dxpData = await _fetchDXPInsurances(products)
      const insurances = _loadCompleteSheetProducts(dxpData, data.listPricing)
      return (
        (productCode ? insurances.find((i) => i.productCode === productCode) : insurances[0]) ??
        null
      )
    } catch (error) {
      if (error instanceof CustomError) {
        error.setDisplayType(errorDisplayType)
      }
      errorManager.handleError(error)

      return null
    } finally {
      loadingStore.stopLoading()
    }
  }

  async function hireProduct(
    quotationId: string | null,
    startDate: Date,
    billingCycle: string,
    product: DentalInsurance,
    role: UserRoleEnum
  ) {
    let valid = true

    try {
      loadingStore.startLoading('common.loading.processing')

      if (!quotationId) throw new Error('Quotation id is required')

      const endpoint = ENDPOINTS.SAVE_QUOTATION_DENTAL
      const request = DentalMapper.SaveQuotationMapper.toRequest(
        quotationId,
        startDate,
        billingCycle,
        product,
        role
      )
      const { error, data } = await MiddlewareService.post(endpoint, request)
      if (error) throw new ServiceError(data)
    } catch (error) {
      console.error(error)
      valid = false
    } finally {
      loadingStore.stopLoading()
    }

    return { valid }
  }

  async function fetchHiringFormAnswers(quotationId: string) {
    try {
      commonStore.showLogo = false
      const endpoint = `${ENDPOINTS.GET_RECOVERY_DENTAL}?quotationId=${quotationId}`
      const { error, data } = await MiddlewareService.get(endpoint)
      if (error) {
        const modalButtons = [new ModalButton({ text: 'copy_button_emission_exit', navTo: 'exit' })]
        throw new ServiceError(data)
          .setDisplayType(ErrorDisplayTypeEnum.MODAL)
          .setButtons(modalButtons)
      }
      await commonStore.resolveMediatorFromRecovery(data.mediator?.mediator)
      await loadConfiguration(true)
      const branchConfig = getBranchConfiguration()
      const itsOk = await checkRecoveredQuotationBranch(
        data.hiring.businessLine,
        branchConfig.businessLineId,
        quotationId
      )
      if (!itsOk) return
      const { productCode, clientCode } = data.hiring
      const product = new ProductId({ productCode, clientCode })
      const dxpData = await _fetchDXPInsurances([product])
      if (!dxpData.length) {
        const modalButtons = [new ModalButton({ text: 'copy_button_emission_exit', navTo: 'exit' })]
        throw new ServiceError(data)
          .setDisplayType(ErrorDisplayTypeEnum.MODAL)
          .setButtons(modalButtons)
      }

      const DXPProduct = dxpData[0]
      const MIDProduct = data.hiring
      const insurance = DentalCommonMapper.GetProductsMapper.fromResponse(DXPProduct, MIDProduct)
      hiringAnswers.value = DentalMapper.GetRecoveryMapper.fromResponse(
        data,
        insurance,
        branchConfig.relationships
      )
    } catch (error) {
      errorManager.handleError(error)
      loadingStore.stopLoading()
    } finally {
      commonStore.showLogo = true
    }
  }

  const availableHiringMethods = {
    sendPolicyHolderDocument,
    checkExistClient,
    sendPolicyHolder,
    sendInsureds,
    sendPayment
  }
  async function callToHiringMethod(
    methodName: keyof typeof availableHiringMethods,
    errorDisplayType: ErrorDisplayTypeEnum = ErrorDisplayTypeEnum.TOAST,
    answers: DentalHiringAnswers
  ) {
    try {
      loadingStore.startLoading('common.loading.processing')

      if (methodName && methodName in availableHiringMethods) {
        await availableHiringMethods[methodName](answers)
      }
    } catch (error) {
      const err = error instanceof CustomError ? error.setDisplayType(errorDisplayType) : error
      errorManager.handleError(err)
      throw error
    }
  }
  function _setStepProcess(stepProcess: DentalInsuranceProcessStepEnum) {
    const currentStepCode = hiringAnswers.value.processStatus.stepCode ?? 0
    if (currentStepCode < stepProcess) {
      hiringAnswers.value.processStatus.stepCode = stepProcess
    }
  }
  async function sendPolicyHolderDocument(answers: DentalHiringAnswers) {
    if (answers.currentInsured?.role === UserRoleEnum.INSURED) return
    const endpoint = ENDPOINTS.SAVE_DOCUMENT_DENTAL
    const quotationId = answers.quotationId ?? ''
    const holder = answers.holder as DentalHiringInsured
    const request = await DentalMapper.SaveDocumentMapper.toRequest(quotationId, holder)
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    _setStepProcess(DentalInsuranceProcessStepEnum.HOLDER)
    toastStore.successToast('health.saved-holder-data-and-document')
  }

  // TODO: Pending to remove
  async function checkExistClient(answers: DentalHiringAnswers) {
    const holder = answers.holder
    const endpoint = ENDPOINTS.MDW_HEALTH_EXIST_CLIENT
    const request = DentalMapper.ExistClientMapper.toRequest(holder)
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    const thirdPartiesAnswered = data.hasDataPD && data.clientExists
    holder.updateAttributes({ thirdPartiesAnswered })
    setGloballyAnswers({ holder })
  }

  async function sendPolicyHolder(answers: DentalHiringAnswers) {
    const endpoint = ENDPOINTS.SAVE_HOLDER_DENTAL
    const quotationId = answers.quotationId ?? ''
    const holder = answers.holder
    const request = DentalMapper.SavePolicyHolderMapper.toRequest(quotationId, holder)
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    const oneInsured = answers.insureds.length === 1
    const onlyInsureHolder = oneInsured && answers.holder.role === UserRoleEnum.HOLDER_INSURED
    const processStep = onlyInsureHolder
      ? DentalInsuranceProcessStepEnum.PAYMENT
      : DentalInsuranceProcessStepEnum.INSUREDS
    _setStepProcess(processStep)

    toastStore.successToast(
      answers.holder.role === UserRoleEnum.HOLDER_INSURED
        ? 'health.saved-holder-and-others-data'
        : 'health.saved-holder-data'
    )
  }

  async function sendInsureds(answers: DentalHiringAnswers) {
    const endpoint = ENDPOINTS.SAVE_INSUREDS_DENTAL
    const quotationId = answers.quotationId ?? ''

    if (answers.currentInsured?.role !== UserRoleEnum.INSURED) return

    const request = DentalMapper.SaveInsuredMapper.toRequest(quotationId, [answers.currentInsured])
    const { error, data } = await MiddlewareService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    const isLastInsured =
      answers.insureds[answers.insureds.length - 1].riskNumber ===
      answers.currentInsured?.riskNumber
    if (isLastInsured) {
      _setStepProcess(DentalInsuranceProcessStepEnum.PAYMENT)
    }
    toastStore.successToast(
      t('dental.saved-insured-data', { riskNumber: answers.currentInsured?.riskNumber })
    )
  }

  async function sendPayment(answers: DentalHiringAnswers) {
    const quotationId = answers.quotationId ?? ''
    const bankDetails = answers.bankDetails

    const { error, data } = await commonStore.sendPayment(
      quotationId,
      bankDetails,
      ENDPOINTS.SAVE_PAYMENT_DENTAL
    )
    if (error) throw new ServiceError(data)

    _setStepProcess(DentalInsuranceProcessStepEnum.SIGN)
  }

  async function emission(quotationId: string) {
    return await commonStore.fetchInsuranceState(
      quotationId,
      'hiring-dental',
      ENDPOINTS.POLICY_EMISSION_DENTAL
    )
  }

  function isProcessExpired(): boolean {
    const statusCode = hiringAnswers.value.processStatus.statusCode
    return statusCode === InsuranceProcessStatusEnum.EXPIRED
  }

  function getBranchName(): string {
    return 'dental'
  }

  return {
    stepsBeforeSaveAndExit,

    getBranchConfiguration,
    loadConfiguration,

    setCurrentInsuredAnswers,
    setGloballyAnswers,
    getHiringAnswers,

    getNewRaterPrice,
    hireProduct,

    isProcessExpired,
    fetchHiringFormAnswers,
    callToHiringMethod,
    emission,
    getBranchName
  }
})
